import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import { ReactNode } from "react";

export const DarkSkeletonTheme = ({ children }: { children: ReactNode }) => (
  <SkeletonTheme
    baseColor="rgba(255,255,255,0.1)"
    highlightColor="rgba(255,255,255,0.18)"
    borderRadius={4}
  >
    {children}
  </SkeletonTheme>
);

export const LightSkeletonTheme = ({ children }: { children: ReactNode }) => (
  <SkeletonTheme
    baseColor="rgba(0,0,0,0.08)"
    highlightColor="rgba(0,0,0,0.14)"
    borderRadius={4}
  >
    {children}
  </SkeletonTheme>
);

export const TextBlock = ({ lines = 4 }: { lines?: number }) => (
  <div className="flex flex-col gap-3 w-full max-w-full min-w-0">
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton
        key={i}
        className="!max-w-full"
        height={18}
        width={i === lines - 1 ? "70%" : "100%"}
      />
    ))}
  </div>
);

export const TitleSkeleton = ({ className = "" }: { className?: string }) => (
  <Skeleton className={`max-w-md w-full ${className}`} height={36} />
);

export const ImageBone = ({
  className = "",
}: {
  className?: string;
}) => {
  const isAbsolutelyPositioned = className.includes("absolute");

  return (
    <span
      className={[
        "inline-block overflow-hidden align-middle",
        !isAbsolutelyPositioned ? "relative" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <Skeleton
        className="!absolute !inset-0 !block !h-full !w-full !leading-none"
        containerClassName="absolute inset-0 block h-full w-full leading-none"
      />
    </span>
  );
};

export const BranchHeroSkeleton = () => (
  <section className="bg-empty pt-16 lg:pt-32 px-10 lg:px-24">
    <div className="mb-8 lg:mb-0 flex justify-center lg:justify-start">
      <TitleSkeleton />
    </div>
    <div className="flex flex-col-reverse lg:flex-row justify-between gap-8">
      <div className="text-lg mt-8 lg:pr-10 flex-1 min-w-0">
        <TextBlock lines={5} />
      </div>
      <ImageBone className="mx-auto lg:mx-0 shrink-0 h-40 lg:h-64 w-auto aspect-square" />
    </div>
  </section>
);

export const BranchMetaSkeleton = () => (
  <section className="bg-empty pt-16 px-10 lg:px-24">
    <div className="flex flex-col lg:flex-row justify-between gap-6">
      {[0, 1].map((i) => (
        <div key={i} className="flex items-center gap-6">
          <Skeleton circle width={60} height={60} />
          <div className="flex flex-col gap-2">
            <Skeleton height={28} width={140} />
            <Skeleton height={22} width={200} />
          </div>
        </div>
      ))}
    </div>
  </section>
);

export const BranchContentSkeleton = ({ lines = 8 }: { lines?: number }) => (
  <section className="bg-empty pt-16 pb-10 px-10 lg:px-24">
    <div className="mb-8">
      <Skeleton height={28} width={220} />
    </div>
    <TextBlock lines={lines} />
  </section>
);

export const UniformeSkeleton = () => (
  <>
    <div className="border-footer" />
    <LightSkeletonTheme>
      <section className="full bg-white px-10 lg:px-24 pb-10">
        <div className="flex justify-center pt-8 mb-8">
          <Skeleton height={28} width={240} />
        </div>
        <div className="max-w-md mx-auto w-full">
          <Skeleton className="!w-full" height={320} />
        </div>
      </section>
    </LightSkeletonTheme>
  </>
);
